import React from 'react';
import AutoRajaPageNav from './AutoRajaPageNav';

const HERO_LOGO = '/logos/marketing/logo_landscape_1600x900_safe.png';
const APP_STORE_URL = 'https://apps.apple.com/us/app/autoraja/id6762328981';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.mixout.autoraja';

const FEATURES = [
  {
    title: 'Easy driving controls',
    description: 'Pick up and play in seconds. The controls are designed for quick sessions on the go.',
  },
  {
    title: 'Fast arcade gameplay',
    description: 'Short, intense runs keep every attempt fresh. Jump in, beat your last score, jump out.',
  },
  {
    title: 'Dodge traffic, pull quick turns',
    description: 'Weave through unpredictable traffic and pull tight turns to stay alive a little longer.',
  },
  {
    title: 'Coins and high scores',
    description: 'Collect coins as you drive and chase your next high score on every attempt.',
  },
  {
    title: 'Music pickups',
    description: 'Trigger lively music pickups that match the rhythm and color of the streets.',
  },
  {
    title: 'Customize your auto',
    description: 'Dress up your autorickshaw with cosmetic upgrades and make it your own.',
  },
];

const SCREENSHOTS = [
  {
    src: '/Screenshots/AutoRaja_Burst_20260426_194652_13.png',
    alt: 'AutoRaja gameplay screenshot showing the auto-rickshaw driving through traffic',
  },
  {
    src: '/Screenshots/AutoRaja_Burst_20260426_194821_03.png',
    alt: 'AutoRaja gameplay screenshot with street traffic and collectible coins',
  },
  {
    src: '/Screenshots/AutoRaja_Burst_20260426_195741_01.png',
    alt: 'AutoRaja gameplay screenshot showing an active driving run',
  },
  {
    src: '/Screenshots/AutoRaja_Burst_20260426_200022_17.png',
    alt: 'AutoRaja gameplay screenshot with the player auto-rickshaw on a city road',
  },
];

const AutoRajaLanding = ({ navigateToPath }) => (
  <main className="content">
    <section className="section hero-section autoraja-hero">
      <div className="container">
        <AutoRajaPageNav route="autoraja" navigateToPath={navigateToPath} />

        <div className="hero-grid autoraja-hero-grid">
          <div className="hero-copy">
            <p className="eyebrow">A Mixout Technologies Game</p>
            <h1>AutoRaja</h1>
            <p className="hero-description">
              A fast-paced auto-rickshaw driving adventure through lively streets,
              traffic, coins, hazards, music, and customization.
            </p>
            <div className="hero-actions">
              <a
                href={APP_STORE_URL}
                className="cta-button"
                target="_blank"
                rel="noopener noreferrer"
              >
                Download on the App Store
              </a>
              <a
                href={PLAY_STORE_URL}
                className="cta-button"
                target="_blank"
                rel="noopener noreferrer"
              >
                Get it on Google Play
              </a>
              <button
                type="button"
                className="secondary-button"
                onClick={() => navigateToPath('/autoraja/support')}
              >
                Get Support
              </button>
            </div>
          </div>

          <aside className="hero-card hero-brand-card" aria-label="AutoRaja at a glance">
            <div className="hero-logo-shell">
              <img
                src={HERO_LOGO}
                alt="AutoRaja logo"
                className="hero-product-logo"
              />
            </div>
            <p className="hero-card-label">At A Glance</p>
            <ul className="hero-card-list">
              <li>Endless arcade driving</li>
              <li>Short, replayable sessions</li>
              <li>Available now on the App Store</li>
              <li>Available now on Google Play</li>
            </ul>
          </aside>
        </div>
      </div>
    </section>

    <section id="gameplay" className="section section-highlight">
      <div className="container">
        <div className="section-heading">
          <p className="section-kicker">The Game</p>
          <h2>Weave through the chaos.</h2>
        </div>
        <p className="lead">
          AutoRaja is a fast-paced endless driving game inspired by the energy,
          color, and chaos of lively streets. Jump into your autorickshaw, weave
          through traffic, make quick turns, collect coins, avoid hazards, trigger
          music, and customize your auto.
        </p>
        <div className="feature-grid">
          {FEATURES.map((feature) => (
            <article key={feature.title} className="feature-card">
              <h3>{feature.title}</h3>
              <p>{feature.description}</p>
            </article>
          ))}
        </div>
      </div>
    </section>

    <section id="flavor" className="section">
      <div className="container">
        <div className="section-heading">
          <p className="section-kicker">Why It Feels Unique</p>
          <h2>Lively. Colorful. Relentless.</h2>
        </div>
        <div className="copy-grid">
          <p>
            AutoRaja is built to feel lively, colorful, and exciting. Every run is
            about quick reflexes, smart movement, and seeing how much farther you
            can go than last time.
          </p>
          <p>
            The streets are loud, the traffic is unpredictable, and the music
            keeps the momentum going. Customize your auto, settle in for a quick
            run, and try to beat your last attempt.
          </p>
        </div>
      </div>
    </section>

    <section id="media" className="section section-highlight">
      <div className="container">
        <div className="section-heading">
          <p className="section-kicker">Media</p>
          <h2>Screenshots</h2>
        </div>
        <p className="lead">
          See AutoRaja in motion across traffic, coins, hazards, music pickups,
          and quick arcade runs.
        </p>
        <div className="screenshots-grid">
          {SCREENSHOTS.map((screenshot) => (
            <figure key={screenshot.src} className="screenshot-frame">
              <img src={screenshot.src} alt={screenshot.alt} loading="lazy" />
            </figure>
          ))}
        </div>
      </div>
    </section>

    <section id="platforms" className="section">
      <div className="container">
        <div className="section-heading">
          <p className="section-kicker">Platforms</p>
          <h2>Available on iOS &amp; Android.</h2>
        </div>
        <div className="platform-grid">
          <article className="platform-card">
            <p className="platform-eyebrow">Apple</p>
            <h3>iOS</h3>
            <p>Available now on the App Store for iPhone and iPad.</p>
            <a
              href={APP_STORE_URL}
              className="platform-badge platform-badge-link"
              target="_blank"
              rel="noopener noreferrer"
            >
              View on App Store
            </a>
          </article>
          <article className="platform-card">
            <p className="platform-eyebrow">Google</p>
            <h3>Android</h3>
            <p>Available now on Google Play for Android devices.</p>
            <a
              href={PLAY_STORE_URL}
              className="platform-badge platform-badge-link"
              target="_blank"
              rel="noopener noreferrer"
            >
              View on Google Play
            </a>
          </article>
        </div>
      </div>
    </section>

    <section id="resources" className="section section-highlight">
      <div className="container">
        <div className="section-heading">
          <p className="section-kicker">Resources</p>
          <h2>Help &amp; Policies</h2>
        </div>
        <div className="resource-grid">
          <a
            href="/autoraja/support"
            className="resource-card"
            onClick={(event) => {
              event.preventDefault();
              navigateToPath('/autoraja/support');
            }}
          >
            <h3>Support</h3>
            <p>Get help with gameplay, ads, performance, bugs, or purchases.</p>
            <span className="resource-link-label">Visit Support →</span>
          </a>
          <a
            href="/autoraja/privacy"
            className="resource-card"
            onClick={(event) => {
              event.preventDefault();
              navigateToPath('/autoraja/privacy');
            }}
          >
            <h3>Privacy Policy</h3>
            <p>How we collect, use, and retain information in connection with AutoRaja.</p>
            <span className="resource-link-label">Read Policy →</span>
          </a>
          <a
            href="/autoraja/delete-data"
            className="resource-card"
            onClick={(event) => {
              event.preventDefault();
              navigateToPath('/autoraja/delete-data');
            }}
          >
            <h3>Data Deletion</h3>
            <p>Request deletion of data associated with your use of AutoRaja.</p>
            <span className="resource-link-label">Request Deletion →</span>
          </a>
        </div>
      </div>
    </section>

    <section id="developer" className="section">
      <div className="container">
        <div className="section-heading">
          <p className="section-kicker">Developer</p>
          <h2>Made by Mixout Technologies.</h2>
        </div>
        <div className="copy-grid">
          <p>
            AutoRaja is published by Mixout Technologies Private Limited, an
            Indian software company focused on mobile games and interactive
            products.
          </p>
          <p>
            Have a question, feedback, or press inquiry? Write to us at{' '}
            <a href="mailto:contact@mixout.in">contact@mixout.in</a> or visit the{' '}
            <a
              href="/"
              onClick={(event) => {
                event.preventDefault();
                navigateToPath('/');
              }}
            >
              Mixout homepage
            </a>
            .
          </p>
        </div>
      </div>
    </section>
  </main>
);

export default AutoRajaLanding;
